import { apiFetch } from '@ami/react/core';
import _ from 'lodash';
import moment from 'moment';
import { QuoteInstallationLocationOption } from '../quotes/quoteInstallationInformation';
import { couplerDecision } from '../quotes/otherQuoteEntities';
import { ProductMarketing } from '../products/productGroup';
import { SubLineItem } from '../subLineItem';
import { roundNumberToNearestCent } from '@ami/react/core';
import OverallDiscount from '../overallDiscount';
import NotificationSettings from '../notificationSettings';
import { AttachmentPointBase } from '../quotes/otherQuoteEntities';


export const ShipmentStatus = Object.freeze({
  Scheduled: 'Scheduled',
  Confirmed: 'Confirmed',
  Shipped: 'Shipped',
  Cancelled: 'Cancelled',
});

export const orderStatus = Object.freeze({
  cancelled: { id: 'Cancelled', label: 'Cancelled' },
  pending: { id: 'Pending', label: 'Pending' },
  shippingCompleted: { id: 'ShippingCompleted', label: 'Shipped' },
});

export const defaultMsrpMarkupPercentage = 25;

export class OrderSummary {
  constructor(item) {
    this.id = item.id;
    this.orderDate = item.orderDate;
    this.dealerBranchName = item.dealerBranchName;
    this.dealerContactName = item.dealerContactName;
    this.e2CustomerNumber = item.e2CustomerNumber;
    this.salesPersonName = item.salesPersonName;
    this.workOrderNumber = item.workOrderNumber;
    this.poNumber = item.poNumber;
    this.make = item.make;
    this.model = item.model;
    this.status = item.status;
    this.shipments = item.shipments;
    this.name = item.name;
  }
}

export class OrderView {
  constructor(json) {
    Object.keys(json).map((key) => (this[key] = json[key]));
  }

  getRevisionPdfDownloadUrl(revisionNumber) {
    return apiFetch(`orders/${this.id}/revision/${revisionNumber}`);
  }

  static async getApprovalRequestId(orderId) {
    return await apiFetch(`orders/${orderId}/ApprovalRequestId`, 'POST');
  }
}

export class OrderInput {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.quoteNumber = item.quoteNumber;
      this.isFromQuote = item.isFromQuote;
      this.quoteShippingInstructions = item.quoteShippingInstructions;
      this.poNumber = item.poNumber;
      this.workOrderNumber = item.workOrderNumber;
      this.dealerBranchId = item.dealerBranchId;
      this.dealerBranchName = item.dealerBranchName;
      this.priceZoneId = item.priceZoneId;
      this.dealerContactId = item.dealerContactId;
      this.dealerContactName = item.dealerContactName;
      this.salesPersonId = item.salesPersonId;
      this.territoryManagerId = item.territoryManagerId;
      this.paymentTerms = item.paymentTerms;
      this.notes = item.notes;
      this.privateNotes = item.privateNotes;
      this.billingInstructions = item.billingInstructions;
      this.machineInfo = item.machineInfo;
      this.attachmentPoints = item.attachmentPoints;
      this.products = item.products;
      this.customLineItems = item.customLineItems;
      this.shipments = item.shipments;
      this.name = item.name;
      this.installation = item.installation;
      this.freightSurcharge = item.freightSurcharge;
      this.overallDiscounts = item.overallDiscounts;
      this.notificationSettings = item.notificationSettings;
      this.orderGroupId = item.orderGroupId;
      this.orderGroupName = item.orderGroupName;
      this.useOrderGroup = item.useOrderGroup;
      this.quoteGroupId = item.quoteGroupId;
      this.quoteGroupName = item.quoteGroupName;
      this.industryId = item.industryId;
      this.industryName = item.industryName;
      this.orderNumber = item.orderNumber;
      return;
    }
    this.id = '';
    this.isFromQuote = false;
    this.quoteNumber = '';
    this.quoteShippingInstructions = '';
    this.poNumber = '';
    this.workOrderNumber = '';
    this.dealerBranchId = '';
    this.dealerBranchName = '';
    this.dealerContactId = '';
    this.dealerContactName = '';
    this.salesPersonId = '';
    this.territoryManagerId = '';
    this.paymentTerms = '';
    this.notes = '';
    this.privateNotes = '';
    this.billingInstructions = '';
    this.machineInfo = new OrderMachineInfo();
    this.attachmentPoints = [];
    this.products = [];
    this.customLineItems = [];
    this.shipments = [];
    this.name = '';
    this.installation = new OrderInstallation();
    this.freightSurcharge = 0;
    this.overallDiscounts = [];
    this.notificationSettings = new NotificationSettings();
    this.orderGroupId = null;
    this.orderGroupName = '';
    this.useOrderGroup = false;
    this.quoteGroupId = null;
    this.quoteGroupName = '';
    this.industryId = null;
    this.industryName = '';
    this.orderNumber = null;
  }

  static mapJsonToOrderInput(json) {
    return Object.assign(new OrderInput(), json, {
      machineInfo: new OrderMachineInfo(json.machineInfo),
      attachmentPoints: json.attachmentPoints.map((x) =>
        OrderAttachmentPoint.fromServerEntity(x)
      ),
      products: json.products.map((x) => new OrderProduct(x)),
      customLineItems: json.customLineItems.map(
        (x) => new OrderCustomLineItem(x)
      ),
      shipments: json.shipments.map(
        (x) =>
          new OrderShipment({
            id: x.id,
            name: x.name,
            shipmentDate: x.shipmentDate ? moment(x.shipmentDate) : null,
            status: x.status,
            customLocation: x.customLocation,
            customInstruction : x.customInstruction
          })
      ),
      installation: Object.assign(new OrderInstallation(), json.installation, {
        installationOptions: json.installation
          ? json.installation.installationOptions.map(
              (x) => new OrderInstallationOption(x)
            )
          : [],
      }),
      overallDiscounts: json.overallDiscounts.map(
        (x) => new OverallDiscount(x)
      ),
      notificationSettings: new NotificationSettings(json.notificationSettings),
    });
  }

  static async fromQuote(quoteId) {
    const json = await apiFetch(`orders/from-quote/${quoteId}`);
    return OrderInput.mapJsonToOrderInput(json);
  }

  static async lineItemFromQuoteProduct(quoteProduct) {
    const json = await apiFetch(
      'orders/from-quote-product',
      'POST',
      quoteProduct,
      { automaticSuccessNotification: false }
    );
    return new OrderProduct(json);
  }

  static async getForEdit(orderId) {
    const json = await apiFetch(`orders/${orderId}`);
    return OrderInput.mapJsonToOrderInput(json);
  }

  getProductsSubtotal(msrpMarkupPercentage) {
    return this.products.reduce(
      (prevTotal, product) => {
        return {
          price: prevTotal.price + product.getTotal(),
          msrp: prevTotal.msrp + product.getTotalMsrp(msrpMarkupPercentage),
        };
      },
      { price: 0, msrp: 0 }
    );
  }

  getCustomLineItemsSubTotal(msrpMarkupPercentage) {
    return this.customLineItems.reduce(
      (prevTotal, customLineItem) => {
        return {
          price: prevTotal.price + customLineItem.getTotal(),
          msrp:
            prevTotal.msrp + customLineItem.getTotalMsrp(msrpMarkupPercentage),
        };
      },
      { price: 0, msrp: 0 }
    );
  }

  getInstallationSubTotal(msrpMarkupPercentage) {
    const subTotal = this.installation.installationOptions.reduce(
      (prevTotal, entry) => {
        return {
          price: prevTotal.price + entry.getTotal(),
          msrp: prevTotal.msrp + entry.getTotalMsrp(msrpMarkupPercentage),
        };
      },
      { price: 0, msrp: 0 }
    );

    subTotal.price = subTotal.price + this.installation.travelExpense;
    subTotal.msrp =
      subTotal.msrp +
      this.installation.getTravelExpenseMsrp(msrpMarkupPercentage);

    return subTotal;
  }

  getFreightSurchargeMsrp(msrpMarkupPercentage) {
    const percentage = msrpMarkupPercentage || defaultMsrpMarkupPercentage;
    return roundNumberToNearestCent(
      (this.freightSurcharge * (percentage + 100)) / 100
    );
  }

  getSubTotal() {
    return (
      this.getProductsSubtotal().price +
      this.freightSurcharge +
      this.getCustomLineItemsSubTotal().price +
      this.getInstallationSubTotal().price
    );
  }

  getSubTotalMsrp(msrpMarkupPercentage) {
    return (
      this.getProductsSubtotal(msrpMarkupPercentage).msrp +
      this.getFreightSurchargeMsrp(msrpMarkupPercentage) +
      this.getCustomLineItemsSubTotal(msrpMarkupPercentage).msrp +
      this.getInstallationSubTotal(msrpMarkupPercentage).msrp
    );
  }

  getDiscountedSubTotal() {
    let partialSubTotal = this.getSubTotal();
    this.overallDiscounts.forEach((discount) => {
      partialSubTotal -= discount.getDiscountAmount(partialSubTotal);
    });
    return partialSubTotal;
  }

  getDiscountSubTotalMsrp(msrpMarkupPercentage) {
    return this.getSubTotalMsrp(msrpMarkupPercentage);
  }

  getTax(taxRate) {
    return roundNumberToNearestCent(
      (this.getDiscountedSubTotal() * taxRate) / 100
    );
  }

  getTaxMsrp(taxRate, msrpMarkupPercentage) {
    return roundNumberToNearestCent(
      (this.getSubTotalMsrp(msrpMarkupPercentage) * taxRate) / 100
    );
  }

  getTotal(taxRate) {
    return this.getDiscountedSubTotal() + this.getTax(taxRate);
  }

  getTotalMsrp(taxRate, msrpMarkupPercentage) {
    return (
      this.getSubTotalMsrp(msrpMarkupPercentage) +
      this.getTaxMsrp(taxRate, msrpMarkupPercentage)
    );
  }
}

export class OrderMachineInfo {
  constructor(item) {
    if (item) {
      this.carrierId = item.carrierId;
      this.weightCategoryId = item.weightCategoryId;
      this.make = item.make;
      this.model = item.model;
      this.machineId = item.machineId;
      return;
    }
    this.carrierId = null;
    this.weightCategoryId = null;
    this.make = null;
    this.model = null;
    this.machineId = null;
  }
  isCompleted() {
    return (
      !!this.carrierId &&
      !!this.weightCategoryId &&
      !!this.make &&
      !!this.model &&
      !!this.machineId
    );
  }
}

export class OrderAttachmentPoint extends AttachmentPointBase {
  constructor(id) {
    super(null);
    this.id = id;
    this._initializeCouplerDecision(null);
  }

  static fromServerEntity(json) {
    const newEntity = new OrderAttachmentPoint();
    newEntity.id = json.id;
    newEntity.couplerDecision = json.couplerDecision;
    newEntity.couplerStyleId = json.couplerStyleId;
    newEntity.couplerOilReady = json.couplerOilReady;
    newEntity.machineOemThumbMount = json.machineOemThumbMount;
    newEntity.boltPattern = json.boltPattern;
    newEntity.boltOnMount = json.boltOnMount;
    return newEntity;
  }

  _initializeCouplerDecision(couplerDecision) {
    this.couplerDecision = couplerDecision;
    this.couplerStyleId = null;
    this.couplerOilReady = null;
    this.machineOemThumbMount = null;
    this.boltOnMount = null;
    this.boltPattern = null;
  }

  setDirectPinOn() {
    this._initializeCouplerDecision(couplerDecision.directPinOn);
  }

  setExistingCoupler(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.existingCoupler);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setAmiCoupler(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.amiCoupler);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setExistingTiltRotator(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.existingTiltRotator);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setAmiTiltRotator(couplerStyleId, couplerOilReady) {
    this._initializeCouplerDecision(couplerDecision.amiTiltRotator);
    this.couplerStyleId = couplerStyleId;
    this.couplerOilReady = couplerOilReady;
  }

  setThumbMount(machineOemThumbMount, boltPattern, boltOnMount) {
    this.machineOemThumbMount = machineOemThumbMount;
    this.boltPattern = boltPattern;
    this.boltOnMount = boltOnMount;
  }

  setNotSpecified() {
    this._initializeCouplerDecision(couplerDecision.notSpecified);
  }

  update(newDecision, couplerStyleId, couplerOilReady) {
    const decision = newDecision || this.couplerDecision;
    if (decision === couplerDecision.directPinOn) {
      this.setDirectPinOn();
    } else if (decision === couplerDecision.amiCoupler) {
      this.setAmiCoupler(couplerStyleId, couplerOilReady);
    } else if (decision === couplerDecision.existingCoupler) {
      this.setExistingCoupler(couplerStyleId, couplerOilReady);
    } else if (decision === couplerDecision.amiTiltRotator) {
      this.setAmiTiltRotator(couplerStyleId, couplerOilReady);
    } else if (decision === couplerDecision.existingTiltRotator) {
      this.setExistingTiltRotator(couplerStyleId, couplerOilReady);
    } else if (decision === couplerDecision.notSpecified) {
      this.setNotSpecified();
    } else if (decision === null) {
      this._initializeCouplerDecision(null);
    } else {
      throw new Error('Invalid coupler decision');
    }
  }

  clone() {
    return _.cloneDeep(this);
  }
}

export class OrderContact {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.fullName = item.fullName;
      this.email = item.email;
      return;
    }
    this.id = undefined;
    this.fullName = '';
    this.email = '';
  }
}

export class OrderLineItem {
  constructor(item) {
    if (new.target === OrderLineItem) {
      throw new TypeError('Cannot construct OrderLineItem instances directly');
    }
    if (item) {
      this.id = item.id;
      this.partNumber = item.partNumber;
      this.name = item.name;
      this.details = item.details;
      this.quantity = item.quantity;
      this.price = item.price;
      this.imageKey = item.imageKey;
      this.imageUrl = item.imageUrl;
      this.subLineItems = item.subLineItems.map((x) => new SubLineItem(x));
      const dealerDiscountItem = this.subLineItems.reduce((acc, item, index) => {
        if (item.description.includes("Dealer Discount")) {
          acc.push(index);
        }
        return acc;
      }, []);
      dealerDiscountItem.reverse().forEach((index) => {
        const discountItem = this.subLineItems.splice(index, 1)[0];
        this.subLineItems.push(discountItem);
      });

      return;
    }
    this.id = undefined;
    this.partNumber = '';
    this.name = '';
    this.details = '';
    this.quantity = 1;
    this.price = 0;
    this.imageKey = null;
    this.imageUrl = null;
    this.subLineItems = [];
  }

  getDealerItemIds(json) {
    const missing = json.quantity - json.dealerItemIds.length;
    for (let i = 0; i < missing; i++) {
      json.dealerItemIds.push('');
    }
    return json.dealerItemIds.slice(0, json.quantity);
  }

  clearInventoryItem() {
    this.inventoryItemId = null;
    this.serialNumbers = this.getSerialNumbers({
      quantity: this.quantity,
      serialNumbers: [],
    });
  }

  getSerialNumbers(json) {
    const missing = json.quantity - json.serialNumbers.length;
    const startingIndex = json.serialNumbers.length;
    for (let i = 0; i < missing; i++) {
      if (
        json.availableSerialNumbers &&
        json.availableSerialNumbers.length > i + startingIndex
      ) {
        json.serialNumbers.push(
          json.availableSerialNumbers.sort()[i + startingIndex]
        );
      } else {
        json.serialNumbers.push('');
      }
    }
    return json.serialNumbers.slice(0, json.quantity);
  }

  getBaseMsrp(msrpMarkupPercentage) {
    const percentage = msrpMarkupPercentage || defaultMsrpMarkupPercentage;
    return roundNumberToNearestCent((this.price * (percentage + 100)) / 100);
  }

  getMsrp(msrpMarkupPercentage) {
    let msrp = this.getBaseMsrp(msrpMarkupPercentage);
    this.subLineItems.forEach(
      (subLineItem) => (msrp = msrp + subLineItem.getMsrp(msrpMarkupPercentage))
    );
    return msrp;
  }

  getTotalMsrp(msrpMarkupPercentage) {
    return this.getMsrp(msrpMarkupPercentage) * this.quantity;
  }

  getUnitPrice() {
    let unitPrice = this.price ? parseFloat(this.price) : 0;
    this.subLineItems.forEach(
      (subLineItem) => (unitPrice = unitPrice + subLineItem.price)
    );
    return unitPrice;
  }

  getTotal() {
    const unitPrice = this.getUnitPrice();
    return unitPrice * this.quantity;
  }
}

export class OrderProduct extends OrderLineItem {
  constructor(item) {
    super(item);
    if (item) {
      this.shipmentId = item.shipmentId;
      this.inventoryItemId = item.inventoryItemId;
      this.isConsignment = item.isConsignment;
      this.consignmentDealerBranchId = item.consignmentDealerBranchId;
      this.consignmentDealerBranchName = item.consignmentDealerBranchName;
      this.marketing = new ProductMarketing(item.marketing);
      this.dealerItemIds = this.getDealerItemIds(item);
      this.serialNumbersEnabled = item.serialNumbersEnabled;
      this.serialNumbersAutoGenerate = item.serialNumbersAutoGenerate,
      this.serialNumbers = this.getSerialNumbers(item);
      this.options = item.options;
      this.instructions = item.instructions;
      this.paintColor = item.paintColor;
      this.inventoryItemPaintColor = item.inventoryItemPaintColor;
      this.dimensions = item.dimensions;
      this.lugging = item.lugging;
      this.availableSerialNumbers = item.availableSerialNumbers;
      //HACK: Only used when passed from the server to the client on GetLineItemFromQuoteProduct endpoint.
      this.shipmentDate = item.shipmentDate;
      this.hasCustomerApprovals = item.hasCustomerApprovals;
      this.engineeringLeadTime = item.engineeringLeadTime;
      return;
    }
    this.shipmentId = undefined;
    this.inventoryItemId = undefined;
    this.marketing = new ProductMarketing();
    this.dealerItemIds = [];
    this.serialNumbersEnabled = false;
    this.serialNumbersAutoGenerate = false;
    this.serialNumbers = [];
    this.options = [];
    this.instructions =[];
    this.dimensions = [];
    this.lugging = '';
    this.paintColor = '';
    this.inventoryItemPaintColor = '';
    this.availableSerialNumbers = [];
    this.shipmentDate = undefined;
    this.hasCustomerApprovals = false;
    this.engineeringLeadTime = new OrderProductEngineeringLeadTime();
  }
}

export class OrderCustomLineItem extends OrderLineItem {}

export class OrderShipment {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.name = item.name;
      this.shipmentDate = item.shipmentDate;
      this.customInstruction = item.customInstruction;
      this.customLocation = item.customLocation;
      this.status = item.status;
      return;
    }
    this.id = undefined;
    this.name = undefined;
    this.shipmentDate = undefined;
    this.customInstruction = undefined;
    this.customLocation = undefined;
    this.status = ShipmentStatus.Scheduled;
  }
}

export class OrderShipmentSummary {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.orderId = item.orderId;
      this.workOrderNumber = item.workOrderNumber;
      this.shipmentDate = item.shipmentDate;
      this.products = item.products;
      this.markedForConfirm = item.markedForConfirm
        ? item.markedForConfirm
        : false;
      return;
    }
    this.id = undefined;
    this.orderId = '';
    this.workOrderNumber = '';
    this.shipmentDate = undefined;
    this.products = [];
    this.markedForConfirm = false;
  }
}

export class OrderInstallation {
  constructor(item) {
    if (item) {
      this.isLocationAmiPlant = item.isLocationAmiPlant;
      this.locationId = item.locationId;
      this.location = item.location;
      this.priceZoneId = item.priceZoneId;
      this.travelExpense = item.travelExpense;
      this.installationOptions = item.installationOptions;
      return;
    }
    this.isLocationAmiPlant = undefined;
    this.locationId = null;
    this.location = null;
    this.priceZoneId = null;
    this.travelExpense = 0;
    this.installationOptions = [];
  }

  clone() {
    return new OrderInstallation({ ...this });
  }

  setAmiPlantLocation(locationId) {
    this.isLocationAmiPlant = true;
    this.locationId = locationId;
    this.location = null;
    this.travelExpense = 0;
    return this;
  }

  setLocation(locationId, location = null, travelExpense = 0) {
    this.isLocationAmiPlant = false;
    this.locationId = locationId;
    this.location = location;
    this.travelExpense = travelExpense;
    return this;
  }

  clearLocation() {
    this.isLocationAmiPlant = undefined;
    this.locationId = null;
    this.location = null;
    this.travelExpense = 0;
    return this;
  }

  getTravelExpenseMsrp(msrpMarkupPercentage) {
    const percentage = msrpMarkupPercentage || defaultMsrpMarkupPercentage;
    return roundNumberToNearestCent(
      (this.travelExpense * (percentage + 100)) / 100
    );
  }
}

export class OrderInstallationOption {
  constructor(item) {
    if (item) {
      this.installationOptionId = item.installationOptionId;
      this.label = item.label;
      this.description = item.description;
      this.partNumber = item.partNumber;
      this.quantity = item.quantity;
      this.price = item.price;
      this.discountPercentage = item.discountPercentage;
      return;
    }
    this.installationOptionId = undefined;
    this.label = '';
    this.description = '';
    this.partNumber = '';
    this.quantity = 1;
    this.price = 0;
    this.discountPercentage = 0;
  }

  getMsrp(msrpMarkupPercentage) {
    const percentage = msrpMarkupPercentage || defaultMsrpMarkupPercentage;
    return roundNumberToNearestCent((this.price * (percentage + 100)) / 100);
  }

  getDealerDiscount() {
    return this.price - this.getUnitPrice();
  }

  getUnitPrice() {
    const discountPercentage = this.discountPercentage
      ? parseFloat(this.discountPercentage)
      : 0;
    return roundNumberToNearestCent(
      (this.price * (100 - discountPercentage)) / 100
    );
  }

  getTotal() {
    return this.getUnitPrice() * this.quantity;
  }

  getTotalMsrp(msrpMarkupPercentage) {
    return this.getMsrp(msrpMarkupPercentage) * this.quantity;
  }
}

export class InstallationTravelInformation {
  constructor(item) {
    if (item) {
      this.installationLocations = item.installationLocations.map(
        (x) => new QuoteInstallationLocationOption(x)
      );
      this.basePlantLocation = item.basePlantLocation;
      this.priceZoneId = item.priceZoneId;
      this.dealerGroupId = item.dealerGroupId;
      return;
    }
    this.installationLocations = [];
    this.basePlantLocation = undefined;
    this.priceZoneId = undefined;
    this.dealerGroupId = undefined;
  }
}

export class DocumentsLookupView {
  constructor(item) {
    if (item) {
      this.orderId = item.orderId;
      this.documentId = item.documentId;
      this.fullDocumentId = item.fullDocumentId;
      this.name = item.name;
      this.isLegacy = item.isLegacy;
      this.serialNumbers = item.serialNumbers;
      return;
    }
  }
}

export class CustomerApproval {
  constructor(item) {
    if (item) {
      this.name = item.name ? item.name : '';
      this.Type = 'Order';
      this.relatedId = item.relatedId;
      this.subRelatedId = item.subRelatedId;
      this.dealerContactIds = item.dealerContactIds
        ? item.dealerContactIds
        : [];
      this.engineeringTeamLeadId = item.engineeringTeamLeadId
        ? item.engineeringTeamLeadId
        : '';
      this.engineeringTeamLeadName = item.engineeringTeamLeadName;
      this.engineeringContactId = item.engineeringContactId
        ? item.engineeringContactId
        : '';
      this.engineeringContactName = item.engineeringContactName;
      this.privateNote = item.privateNote ? item.privateNote : '';
      this.privateDocuments = item.privateDocuments
        ? item.privateDocuments
        : [];
      return;
    }
    this.name = '';
    this.Type = 'Order';
    this.relatedId = '';
    this.subRelatedId = '';
    this.dealerContactIds = [];
    this.engineeringTeamLeadId = '';
    this.engineeringTeamLeadName = '';
    this.engineeringContactId = '';
    this.engineeringContactName = '';
    this.privateNote = '';
    this.privateDocuments = [];
  }
}

export const customerApprovalStatus = Object.freeze({
  waitingForEngineering: {
    id: 'WaitingForEngineering',
    label: 'Waiting for Engineering',
  },
  inEngineeringDesign: {
    id: 'InEngineeringDesign',
    label: 'In Engineering Design',
  },
  engineeringDesignCompleted: {
    id: 'EngineeringDesignCompleted',
    label: 'Engineering Design Completed',
  },
  publishedToDealer: { id: 'PublishedToDealer', label: 'Published to Dealer' },
  waitingForApproval: {
    id: 'WaitingForApproval',
    label: 'Waiting for Approval',
  },
  approved: { id: 'Approved', label: 'Approved' },
  rejected: { id: 'Rejected', label: 'Rejected' },
  releasedToProduction: {
    id: 'ReleasedToProduction',
    label: 'Released to Production',
  },
  cancelled: { id: 'Cancelled', label: 'Cancelled' },
});

export class CustomerApprovalDocument {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.name = item.name || '';
      this.customerApprovalId = item.customerApprovalId;
      this.fileKey = item.fileKey;
      this.fileName = item.fileName;
      return;
    }
    this.id = null;
    this.name = '';
    this.customerApprovalId = undefined;
    this.fileKey = undefined;
  }
}

export class CustomerApprovalOrderLookupViewModel {
  constructor(item) {
    if (item) {
      this.isValid = item.isValid;
      this.result = item.result;
      return;
    }
    this.isValid = false;
    this.result = new CustomerApprovalLookupResultView();
  }
}

export class CustomerApprovalLookupResultView {
  constructor(item) {
    if (item) {
      this.id = item.id;
      this.workOrderNumber = item.workOrderNumber;
      this.documents = item.documents;
      this.dealerGroupId = item.dealerGroupId;
      this.poNumber = item.poNumber;
      return;
    }
    this.id = null;
    this.workOrderNumber = '';
    this.documents = [];
    this.dealerGroupId = '';
    this.poNumber = '';
  }
}

export const NotificationScheduleTypes = Object.freeze({
  Immediately: { id: 'Immediately', label: 'Notify immediately' },
  Batch: { id: 'Batch', label: 'Notify by batch email' },
});

export class OrderProductEngineeringLeadTime {
  constructor(json) {
    if (json) {
      this.designTimeHours = json.designTimeHours;
      this.queueTimeHours = json.queueTimeHours;
      this.totalHours = json.totalHours;
      return;
    }

    this.designTimeHours = null;
    this.queueTimeHours = null;
    this.totalHours = 0;
  }
}
